import { FC } from "react";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils/classnames";

interface PropertyGroupOptionBadgeListProps {
  values: string[];
  formatValue?: (value: string) => string;
  maxDisplay?: number;
  isOther?: boolean;
  otherTitle?: string;
  className?: string;
}

export const PropertyGroupOptionBadgeList: FC<
  PropertyGroupOptionBadgeListProps
> = ({
  values = [],
  formatValue = (value) => value,
  maxDisplay = 2,
  isOther = false,
  otherTitle = "",
  className = ""
}) => {
  if (isOther)
    return (
      <Badge
        className="opacity-50"
        variant={BadgeVariant.OUTLINE}
        size={BadgeSize.SM}
      >
        Other {otherTitle}
      </Badge>
    );

  const hasMore = values.length > maxDisplay;
  const displayValues = hasMore ? values.slice(0, maxDisplay) : values;

  if (hasMore)
    return (
      <div
        className={cx(
          "flex gap-1 flex-wrap w-full max-w-full overflow-hidden sm:max-w-[230px]",
          className
        )}
      >
        {displayValues.map((item, idx) => (
          <Badge
            key={`${idx}-${item}`}
            variant={BadgeVariant.OUTLINE}
            size={BadgeSize.SM}
            className="text-content-1000 font-medium overflow-hidden"
          >
            <span className="overflow-hidden text-ellipsis whitespace-nowrap block">
              {formatValue(item)}
            </span>
          </Badge>
        ))}
        <Tooltip
          placement="bottom"
          interactive={true}
          delay={{
            close: 100
          }}
        >
          <TooltipTrigger asChild>
            <div className="flex flex-col">
              <Badge
                variant={BadgeVariant.OUTLINE}
                size={BadgeSize.SM}
                className="text-content-1000 font-medium cursor-pointer"
              >
                +{values.length - maxDisplay} more
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent className="p-0 rounded-lg border-0 bg-transparent shadow-none z-[51]">
            <div className="rounded-lg border border-alpha-100 bg-content-1000-inverted shadow-xs animate-bounce-in max-w-[300px] flex gap-2 p-4 flex-wrap max-h-[200px] overflow-y-auto">
              {values.map((item, idx) => (
                <Badge
                  key={`tooltip-${idx}-${item}`}
                  variant={BadgeVariant.OUTLINE}
                  size={BadgeSize.SM}
                  className="text-content-1000 font-medium"
                >
                  {formatValue(item)}
                </Badge>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    );

  return (
    <div className={`flex gap-1 flex-wrap w-full ${className}`}>
      {values.map((item, idx) => (
        <Badge
          key={`${idx}-${item}`}
          variant={BadgeVariant.OUTLINE}
          size={BadgeSize.SM}
          className="text-content-1000 font-medium mb-1 overflow-hidden"
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap block">
            {formatValue(item)}
          </span>
        </Badge>
      ))}
    </div>
  );
};
